import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Alert as RebaseAlert,
    Button as RebaseButton,
    Card as RebaseCard,
    Chip as RebaseChip,
    TextField as RebaseTextField,
    Typography as RebaseTypography,
    cls,
    defaultBorderMixin,
    iconSize
} from "@rebasepro/ui";
import {
    Send as RebaseSendIcon,
    Sparkles as RebaseSparklesIcon,
    // `AlertTriangle`, not `TriangleAlert`. The two lucide-reacts in play are
    // 0.344 locally and the 1.27 that `@rebasepro/ui` pins inside the Docker
    // build, and 1.x renamed this icon — but kept the old name as an alias.
    // `AlertTriangle` is therefore the only spelling that resolves in both, and
    // `TriangleAlert` typechecks nowhere except production. The mirror image of
    // the `Linkedin` failure that tests/lucide-icons.test.mjs exists for.
    AlertTriangle as RebaseWarnIcon
} from "lucide-react";

import { callFunction, type LinkedEntityType } from "./_email_shared";
import {
    CandidateShortlist,
    shortlistBlock,
    shortlistLabel,
    type ShortlistCandidate
} from "./CandidateShortlist";
import { PresentPanel } from "./PresentCandidates";

const Alert = RebaseAlert as React.ComponentType<any>;
const Button = RebaseButton as React.ComponentType<any>;
const Card = RebaseCard as React.ComponentType<any>;
const Chip = RebaseChip as React.ComponentType<any>;
const TextField = RebaseTextField as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;
const SendIcon = RebaseSendIcon as React.ComponentType<any>;
const SparklesIcon = RebaseSparklesIcon as React.ComponentType<any>;
const WarnIcon = RebaseWarnIcon as React.ComponentType<any>;

/**
 * Writing one email, from wherever in the panel you happen to be.
 *
 * The same component behind "Escribir" on a contact, "Responder" in the inbox
 * and the composer on a candidate's screen. One implementation, because the
 * three differ only in what they already know about the recipient — and because
 * the parts that must not vary are the parts a second copy would get wrong: the
 * draft's provenance, the warnings, and the fact that nothing is sent without
 * somebody pressing the button.
 *
 * ── "Redactar con IA" is a starting point, not a send button ─────────
 *
 * The draft arrives in the same textarea the operator types in, and it is
 * editable before, during and after. It also arrives with the list of facts it
 * was built from and any warnings the review found — an unfilled placeholder, a
 * price, a promise. Both are shown, not hidden behind a disclosure: a draft
 * whose sources are invisible is one nobody checks, and the whole reason a human
 * is in this loop is to be the thing that checks it.
 *
 * Nothing here writes to the database. The send function does that, after SMTP
 * has answered — see functions/send-email.ts.
 */

export interface ComposerTarget {
    /** Prefilled recipients. The operator may change them. */
    to: string[];
    cc?: string[];
    /** Prefilled subject. Empty on a reply — the backend derives "Re: …". */
    subject?: string;
    /** Reply into this conversation, rather than starting one. */
    threadId?: string | null;
    /** What the new conversation is about. Stored as a person's decision. */
    link?: {
        entity_type: Exclude<LinkedEntityType, "none">;
        entity_id?: string | null;
        employer_key?: string | null;
        contact_id?: string | null;
    } | null;
    /** Extra context for the drafter — the same keys `draft-email` accepts. */
    draftContext?: Record<string, unknown>;
    /**
     * Offer a candidate shortlist to name in this message.
     *
     * Only where it means something: writing to an employer. A reply to a
     * candidate does not need one, and putting the picker on every composer
     * would be a control that is wrong three times out of four.
     */
    allowShortlist?: boolean;
    /**
     * The company record behind this employer, when there is one.
     *
     * With it, the candidates named in the shortlist can also be presented in
     * the product — onto the employer's dashboard, with a note each — instead
     * of only named in the text. A separate, explicit button: naming somebody in
     * an email and opening their profile to a company are different acts, and
     * neither implies the other. Most prospects have no company record, and
     * then there is nowhere to present to.
     */
    presentTo?: { id: string; name: string } | null;
    /**
     * What a template's placeholders become. `{{nombre}}` takes the first
     * word of `recipientName`; `{{empresa}}` takes `employerName`. A caller
     * that knows neither leaves them, and the placeholder stays visible in
     * the draft — a thing to fix before sending, rather than a blank greeting
     * that gets sent.
     */
    recipientName?: string | null;
    employerName?: string | null;
}

interface TemplateRow {
    id: string;
    name: string;
    audience?: "candidate" | "employer" | "any" | null;
    subject: string;
    body: string;
}

import { fillTemplate, hasUnfilledClaimLink, hasUnfilledPlaceholder } from "../email_templates";
import { resolveClaimLink } from "./_claim_link";

export function EmailComposer({
    client,
    target,
    canSend,
    onSent,
    onCancel,
    compact,
    onShortlistSent,
    onShortlistPresented
}: {
    client: any;
    target: ComposerTarget;
    /** False when SMTP is not configured — the form is shown, disabled, with why. */
    canSend: boolean;
    onSent: (threadId: string) => void | Promise<void>;
    onCancel?: () => void;
    /** In an entity tab the composer is one block among several. */
    compact?: boolean;
    /** Log the candidates that were named, once the message has actually gone. */
    onShortlistSent?: (candidates: ShortlistCandidate[]) => void | Promise<void>;
    /** Log the candidates that were presented, once the rows exist. */
    onShortlistPresented?: (candidates: ShortlistCandidate[]) => void | Promise<void>;
}) {
    const allowShortlist = target.allowShortlist === true;
    const [to, setTo] = useState(target.to.join(", "));
    const [cc, setCc] = useState((target.cc ?? []).join(", "));
    const [subject, setSubject] = useState(target.subject ?? "");
    const [body, setBody] = useState("");
    const [instruction, setInstruction] = useState("");

    const [drafting, setDrafting] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    const [warnings, setWarnings] = useState<string[]>([]);
    const [facts, setFacts] = useState<string[]>([]);
    const [pickingShortlist, setPickingShortlist] = useState(false);
    const [shortlist, setShortlist] = useState<ShortlistCandidate[]>([]);
    const [presenting, setPresenting] = useState(false);

    /**
     * The team's templates, read once per composer. Filtered to the audience
     * the target implies: a reply to a candidate does not offer "Primer
     * contacto con una empresa". `any` is always offered.
     */
    const [templates, setTemplates] = useState<TemplateRow[]>([]);
    useEffect(() => {
        let live = true;
        (client?.data?.collection("email-templates").find({ orderBy: ["name", "asc"], limit: 100 }) ?? Promise.resolve(undefined))
            .then((page: any) => { if (live) setTemplates((page?.data ?? []) as TemplateRow[]); })
            .catch(() => { if (live) setTemplates([]); });
        return () => { live = false; };
    }, [client]);
    const audience: TemplateRow["audience"] = target.link?.entity_type === "talent" || target.link?.entity_type === "application"
        ? "candidate"
        : target.link?.entity_type === "company" || target.link?.entity_type === "prospect"
            ? "employer"
            : null;
    const offeredTemplates = templates.filter(t => !audience || !t.audience || t.audience === "any" || t.audience === audience);

    /**
     * Why `{{enlace_ficha}}` stayed a placeholder, when it did: no record, two
     * records, or an owner already. Shown beside the hole so the operator knows
     * whether to fix it or delete the sentence.
     */
    const [claimLinkWhy, setClaimLinkWhy] = useState<string | null>(null);
    const templateRun = useRef(0);

    const applyTemplate = useCallback(async (id: string) => {
        const template = templates.find(t => t.id === id);
        if (!template) return;
        const run = ++templateRun.current;

        // The claim link is looked up only for a template that asks for it —
        // two or three reads, and most templates never need them.
        let claimUrl: string | null = null;
        setClaimLinkWhy(null);
        if (hasUnfilledClaimLink(`${template.body}\n${template.subject}`)) {
            const context = (target.draftContext ?? {}) as { entity_type?: unknown; entity_id?: unknown; employer_key?: unknown };
            const companyId = target.link?.entity_type === "company"
                ? target.link.entity_id ?? null
                : context.entity_type === "company" && typeof context.entity_id === "string" ? context.entity_id : null;
            const employerKey = target.link?.employer_key
                ?? (typeof context.employer_key === "string" ? context.employer_key : null);
            const answer = await resolveClaimLink(client?.data, { companyId, employerKey });
            if (run !== templateRun.current) return;
            claimUrl = answer.url;
            setClaimLinkWhy(answer.why);
        }

        const values = { recipientName: target.recipientName, employerName: target.employerName, claimUrl };
        setBody(fillTemplate(template.body, values));
        // A reply keeps the conversation's subject; a new message takes the
        // template's unless the operator already typed one.
        if (!target.threadId && !subject.trim()) setSubject(fillTemplate(template.subject, values));
    }, [templates, client, target.recipientName, target.employerName, target.threadId, target.link, target.draftContext, subject]);

    // Follow the target. Opening the composer on a second contact without this
    // shows the first one's address, which is how an email goes to the wrong
    // person while looking entirely correct on screen.
    useEffect(() => {
        setTo(target.to.join(", "));
        setCc((target.cc ?? []).join(", "));
        setSubject(target.subject ?? "");
        setBody("");
        setWarnings([]);
        setFacts([]);
        setError("");
        // A shortlist chosen for one employer must never survive into a message
        // to another. Naming three candidates to the wrong company is the exact
        // mistake this feature could make, so it is cleared with the address.
        setShortlist([]);
        setPickingShortlist(false);
        setPresenting(false);
        // A template picked for the previous recipient may still be waiting on
        // its claim-link lookup. Moving the run counter on makes it discard its
        // answer when it lands, rather than writing company A's claim link into
        // a message that now goes to company B.
        templateRun.current++;
        setClaimLinkWhy(null);
    }, [target.to.join(","), target.cc?.join(","), target.subject, target.threadId, target.presentTo?.id]);

    const draft = useCallback(async () => {
        setDrafting(true);
        setError("");
        try {
            const result = await callFunction<{
                subject: string; body: string; warnings: string[]; facts: string[];
            }>(client, "draft-email", {
                ...(target.draftContext ?? {}),
                thread_id: target.threadId ?? undefined,
                to: to.split(",")[0]?.trim() || undefined,
                instruction: instruction.trim() || undefined
            });

            setBody(result.body);
            // A reply keeps the conversation's subject, so a drafted one only
            // fills an empty field — overwriting "Re: Vacante" with the model's
            // idea of a subject would fork the thread in the recipient's client.
            if (!subject.trim() && result.subject) setSubject(result.subject);
            setWarnings(result.warnings ?? []);
            setFacts(result.facts ?? []);
        } catch (err: any) {
            setError(err?.message || "No se pudo redactar el borrador");
        } finally {
            setDrafting(false);
        }
    }, [client, target.draftContext, target.threadId, to, instruction, subject]);

    const send = useCallback(async () => {
        setSending(true);
        setError("");
        try {
            const result = await callFunction<{ thread_id: string; warning?: string; rejected?: string[] }>(
                client,
                "send-email",
                {
                    to,
                    cc: cc.trim() ? cc : undefined,
                    subject: subject.trim() || undefined,
                    body,
                    thread_id: target.threadId ?? undefined,
                    link: target.link ?? undefined
                }
            );
            // Reported rather than swallowed: a message SMTP accepted for two of
            // three recipients is not a clean send, and the operator is the only
            // one who can decide what to do about the third.
            if (result.rejected?.length) {
                setError(`Estas direcciones no aceptaron el correo: ${result.rejected.join(", ")}. A las demás sí les llegó — revisa que estén bien escritas y vuelve a enviarlo sólo a ellas.`);
            }
            if (result.warning) setWarnings([result.warning]);
            // After SMTP answered, never before: a log entry saying we named
            // three candidates to an employer, for a message that failed to
            // send, is the same class of lie the send function's own logging
            // exists to avoid.
            const named = shortlist;
            setBody("");
            setInstruction("");
            setShortlist([]);
            setPickingShortlist(false);
            if (named.length > 0) await onShortlistSent?.(named);
            await onSent(result.thread_id);
        } catch (err: any) {
            setError(err?.message || "No se pudo enviar");
        } finally {
            setSending(false);
        }
    }, [client, to, cc, subject, body, target.threadId, target.link, onSent, shortlist, onShortlistSent]);

    const ready = canSend && to.trim().length > 0 && body.trim().length > 0
        && (Boolean(target.threadId) || subject.trim().length > 0);

    return (
        <Card className={cls("flex flex-col gap-3", compact ? "p-3" : "p-4", defaultBorderMixin)}>
            {!canSend && (
                <Alert color="warning" size="small">
                    Todavía no está conectado el buzón desde el que enviamos, así que este mensaje no puede salir. Puedes dejarlo escrito y avisar a quien lleva la configuración.
                    Puedes escribir el borrador, pero no se puede enviar desde aquí.
                </Alert>
            )}

            <div className="flex gap-2 flex-wrap">
                <TextField size="small" label="Para" value={to} className="grow"
                    onChange={(e: any) => setTo(e.target.value)} />
                <TextField size="small" label="Copia (opcional)" value={cc} className="grow"
                    onChange={(e: any) => setCc(e.target.value)} />
            </div>

            <TextField
                size="small"
                label={target.threadId ? "Asunto (se mantiene el de la conversación si lo dejas vacío)" : "Asunto"}
                value={subject}
                onChange={(e: any) => setSubject(e.target.value)}
            />

            {/* ── A template, when one fits ── */}
            {offeredTemplates.length > 0 && (
                <div className="flex gap-2 flex-wrap items-center">
                    <Typography variant="body2" color="secondary">Plantilla</Typography>
                    <select
                        aria-label="Plantilla de correo"
                        className="text-sm rounded-md border px-2 py-1 bg-transparent"
                        value=""
                        disabled={drafting || sending}
                        onChange={(e) => { void applyTemplate(e.target.value); e.target.value = ""; }}
                    >
                        <option value="">Elegir…</option>
                        {offeredTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                    {body && hasUnfilledPlaceholder(body) && (
                        <Typography variant="body2" color="secondary">
                            Quedan huecos {"{{…}}"} por rellenar en el texto.
                            {claimLinkWhy && hasUnfilledClaimLink(body) ? ` ${claimLinkWhy}` : ""}
                        </Typography>
                    )}
                </div>
            )}

            {/* ── The drafting row ── */}
            <div className="flex gap-2 flex-wrap items-end">
                <TextField
                    size="small"
                    label="¿Qué quieres decirle? (opcional)"
                    value={instruction}
                    className="grow"
                    onChange={(e: any) => setInstruction(e.target.value)}
                />
                <Button size="small" variant="outlined" disabled={drafting || sending} onClick={draft}>
                    <SparklesIcon size={iconSize.smallest} />
                    {drafting ? "Redactando…" : (body ? "Rehacer borrador" : "Redactar con IA")}
                </Button>
            </div>

            <TextField
                size="small"
                label="Mensaje"
                multiline
                rows={compact ? 8 : 12}
                value={body}
                onChange={(e: any) => setBody(e.target.value)}
            />

            {/*
                ── The shortlist ──

                Every outreach draft promises candidates. This is the only place
                in the product that can put any behind the promise, and it does
                it the one way that is defensible: a person searches, reads, and
                picks, and what is written down is the message that went out.
                Nothing is scored, suggested or stored as a link between a
                vacancy and somebody who has not applied to it.
            */}
            {allowShortlist && (
                <div className="flex flex-col gap-2">
                    <div className="flex gap-2 items-center flex-wrap">
                        <Button
                            size="small"
                            variant={pickingShortlist ? "filled" : "outlined"}
                            disabled={sending}
                            onClick={() => setPickingShortlist(v => !v)}
                        >
                            {pickingShortlist ? "Dejar de elegir" : "Nombrar candidatos"}
                        </Button>
                        {shortlist.length > 0 && (
                            <>
                                <Chip size="small" colorScheme="greenLighter">
                                    {shortlist.length === 1 ? "1 elegido" : `${shortlist.length} elegidos`}
                                </Chip>
                                <Button
                                    size="small"
                                    variant="text"
                                    disabled={sending}
                                    // Appended rather than substituted into the
                                    // draft: the operator wrote the sentence
                                    // above it and is the one who decides how it
                                    // reads. It lands in the textarea, editable,
                                    // which is the only way it gets read before
                                    // it is sent.
                                    onClick={() => setBody(current =>
                                        `${current.trimEnd()}\n\n${shortlistBlock(shortlist)}\n`)}
                                >
                                    Añadirlos al mensaje
                                </Button>
                                {target.presentTo && (
                                    <Button
                                        size="small"
                                        variant="text"
                                        disabled={sending}
                                        onClick={() => setPresenting(v => !v)}
                                    >
                                        {presenting ? "No presentarlos" : `Presentar a ${target.presentTo.name}`}
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                    {presenting && target.presentTo && shortlist.length > 0 && (
                        <PresentPanel
                            company={target.presentTo}
                            candidates={shortlist.map(c => ({ id: c.id, name: shortlistLabel(c) }))}
                            onPresented={presented => onShortlistPresented?.(
                                shortlist.filter(c => presented.some(p => p.id === c.id))
                            )}
                            onCancel={() => setPresenting(false)}
                        />
                    )}
                    {pickingShortlist && (
                        <CandidateShortlist
                            client={client}
                            selected={shortlist}
                            onChange={setShortlist}
                        />
                    )}
                </div>
            )}

            {/*
                What the draft was built from. Shown rather than tucked away: the
                model may only assert what is in this list, so the list is how an
                operator checks whether a sentence in the draft is a fact or an
                invention — which is the entire safety story of drafting.
            */}
            {facts.length > 0 && (
                <div className="flex flex-col gap-1">
                    <Typography variant="body2" color="secondary">
                        El borrador sólo puede afirmar estos datos, salidos de nuestras propias fichas:
                    </Typography>
                    <ul className="list-disc pl-5">
                        {facts.map(fact => (
                            <li key={fact}>
                                <Typography variant="body2" color="secondary">{fact}</Typography>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {warnings.map(warning => (
                <Alert key={warning} color="warning" size="small">
                    <span className="inline-flex items-center gap-1.5">
                        <WarnIcon size={iconSize.smallest} />
                        {warning}
                    </span>
                </Alert>
            ))}

            {error && <Alert color="error" size="small">{error}</Alert>}

            <div className="flex gap-2 items-center flex-wrap">
                <Button size="small" disabled={!ready || sending || drafting} onClick={send}>
                    <SendIcon size={iconSize.smallest} />
                    {sending ? "Enviando…" : "Enviar"}
                </Button>
                {onCancel && (
                    <Button size="small" variant="text" disabled={sending} onClick={onCancel}>
                        Cancelar
                    </Button>
                )}
                {target.threadId && (
                    <Chip size="small" colorScheme="blueLighter">responde en esta conversación</Chip>
                )}
            </div>
        </Card>
    );
}
